/**
 * Workshop version of ConversationFrame.
 *
 * Wiring (production):
 * - ConversationFrame is the canonical canvas frame for ALL conversation history.
 * - Input always flows through the global InputBar → `prompt.send` WebSocket.
 * - Message history loads via REST: GET /api/v1/.../conversations/<id>/messages/
 * - New messages stream in via canvas WebSocket `stream.event` → frame.updated.
 * - Multi-agent conversations are supported: Message.agent_id identifies the
 *   producing agent per message — a single frame shows messages from any agent.
 * - There is no separate per-agent chat widget. Do not add one.
 */
import { Icons } from "@/components/ui/icons";
import { cn } from "@/lib/cn";
import { FrameHeader } from "@/workshop/components/shapes/shared/frame-header";

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
}

interface ConversationFrameProps {
  messages?: ChatMessage[];
  isStreaming?: boolean;
  title?: string;
  agentName?: string;
  className?: string;
  "data-testid"?: string;
}

const DEFAULT_MESSAGES: ChatMessage[] = [
  { id: "m1", role: "user", content: "What's the latest on React Flow v12?" },
  { id: "m2", role: "assistant", content: "React Flow v12 introduces improved performance with the new rendering engine, better TypeScript support, and a simplified API for custom nodes..." },
];

export function ConversationFrame({
  messages = DEFAULT_MESSAGES,
  isStreaming = true,
  title = "Research Chat",
  agentName = "Assistant",
  className,
  "data-testid": testId = "conversation-frame",
}: ConversationFrameProps) {
  return (
    <div className={cn("denker-glass-frame flex flex-col rounded-frame overflow-hidden", className)} data-testid={testId}>
      <FrameHeader title={title} icon={Icons.MessageCircle} accentColor="bg-frame-conversation" agentName={agentName} statusColor={isStreaming ? "bg-status-streaming" : "bg-muted"} data-testid={`${testId}-header`} />
      <div className="flex flex-1 flex-col gap-0.5 overflow-hidden px-2 py-1">
        {messages.map((msg, idx) => {
          const isUser = msg.role === "user";
          const isLast = idx === messages.length - 1;
          return (
            <div key={msg.id} className={cn("flex w-full", isUser ? "justify-end" : "justify-start")} data-testid={`${testId}-msg-${msg.id}`}>
              <div className={cn("max-w-[75%] px-1 py-1", isUser ? "rounded-tl-lg rounded-tr-lg rounded-bl-sm rounded-br-lg bg-[#044B2B]" : "rounded-tl-lg rounded-tr-lg rounded-bl-lg rounded-br-sm border border-border bg-surface")}>
                <p className={cn("whitespace-pre-wrap text-canvas-sm leading-[1.4]", isUser ? "text-white" : "text-secondary")}>{msg.content}</p>
                {!isUser && isLast && isStreaming && (
                  <span className="mt-0.5 inline-block h-2.5 w-0.5 animate-pulse rounded-sm bg-accent" aria-label="Streaming" />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export type { ConversationFrameProps, ChatMessage };
